import { StickyNote } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { CLIENT_ROOT } from 'src/constants/paths';
import useTabs from 'src/hooks/useTabs';

import {
  addMinutesToClock,
  athenaUrl,
  formatClock12,
  getAppointmentTimezoneAbbreviation,
  getAppointmentTypeColor,
} from '../../helpers';
import BookingRail from '../BookingRail';
import HuddleNotes, { HuddleNoteCount } from '../HuddleNotes';
import HuddleTasksSection from '../HuddleTasksSection';
import MocaFlag, { isLowMoca } from '../MocaFlag';
import ProviderAvatars from '../ProviderAvatars';
import styles from './HuddleVisitCard.module.scss';

const HuddleVisitCard = ({
  appt,
  tasks,
  markingTaskId,
  assigningTaskId,
  onMarkComplete,
  onAssignToMe,
  onOpenTask,
  showNotes = true,
  notes,
  onPostNote,
  onEditNote,
}) => {
  const { onSelectTab } = useTabs();

  const [open, setOpen] = useState(true);
  const patient = appt?.patient;
  const dotColor = getAppointmentTypeColor(appt).dot;

  const localTime = appt?.time;
  const endTime = addMinutesToClock(localTime, appt?.durationMinutes);
  const timezoneAbbreviation = getAppointmentTimezoneAbbreviation(appt);
  const platformHref =
    patient?.id != null ? `${CLIENT_ROOT}/patients/details/${patient.id}/info` : null;
  const athenaHref = athenaUrl(
    patient?.athenaIntegration?.athenaPatientId,
    patient?.athenaIntegration?.practiceId,
  );

  return (
    <div className={styles.row}>
      <BookingRail
        time={localTime}
        durationMinutes={appt?.durationMinutes}
        appointmentTypeId={appt?.appointmentTypeId}
      />
      <div className={styles.card}>
        <div
          role="button"
          tabIndex={0}
          aria-expanded={open}
          className={styles.headerSection}
          onClick={() => setOpen((prev) => !prev)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setOpen((prev) => !prev);
            }
          }}
        >
          <div className={styles.headerRow}>
            <div className={styles.titleLine}>
              <span className={styles.dot} style={{ background: dotColor }} aria-hidden="true" />
              <span className={styles.visitType}>{appt?.visitType}</span>
              <span className={styles.timeSpan}>
                · {formatClock12(localTime)}–{formatClock12(endTime)}
                {timezoneAbbreviation ? ` (${timezoneAbbreviation})` : ''}
              </span>
            </div>
            <div
              className={styles.linkOuts}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <HuddleNoteCount count={notes?.length} />
              {platformHref && (
                <button
                  type="button"
                  className={styles.linkChip}
                  onClick={() => onSelectTab({ path: platformHref })}
                >
                  Platform
                </button>
              )}
              {athenaHref && (
                <a
                  href={athenaHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.linkChip}
                >
                  Athena
                </a>
              )}
            </div>
          </div>

          <div className={styles.metaLine}>
            <span className={styles.memberName}>{patient?.name}</span>
            {appt?.clinician && (
              <>
                <span className={styles.metaDivider}>·</span>
                <ProviderAvatars name={appt.clinician} />
              </>
            )}
            {appt?.caregiverNote && (
              <>
                <span className={styles.metaDivider}>·</span>
                <span>{appt.caregiverNote}</span>
              </>
            )}
          </div>

          {isLowMoca(appt?.latestMoca) && (
            <MocaFlag score={Math.round(appt.latestMoca.score)} className={styles.mocaFlag} />
          )}

          {appt?.athenaScheduleNote && (
            <div className={styles.athenaNote}>
              <StickyNote size={13} className={styles.athenaNoteIcon} aria-hidden="true" />
              <span>
                <span className={styles.athenaNoteLabel}>Athena note ·</span>{' '}
                {appt.athenaScheduleNote}
              </span>
            </div>
          )}
        </div>

        {open && (
          <>
            <HuddleTasksSection
              tasks={tasks}
              markingTaskId={markingTaskId}
              assigningTaskId={assigningTaskId}
              onMarkComplete={onMarkComplete}
              onAssignToMe={onAssignToMe}
              onOpenTask={onOpenTask}
            />

            {showNotes && (
              <HuddleNotes notes={notes} onPostNote={onPostNote} onEditNote={onEditNote} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

HuddleVisitCard.propTypes = {
  appt: PropTypes.shape({
    id: PropTypes.string,
    date: PropTypes.string,
    time: PropTypes.string,
    durationMinutes: PropTypes.number,
    visitType: PropTypes.string,
    appointmentTypeId: PropTypes.string,
    clinician: PropTypes.string,
    patient: PropTypes.object,
    caregiverNote: PropTypes.string,
    athenaScheduleNote: PropTypes.string,
    latestMoca: PropTypes.shape({ score: PropTypes.number, maxScore: PropTypes.number }),
  }).isRequired,
  tasks: PropTypes.array,
  markingTaskId: PropTypes.string,
  assigningTaskId: PropTypes.object,
  onMarkComplete: PropTypes.func.isRequired,
  onAssignToMe: PropTypes.func.isRequired,
  onOpenTask: PropTypes.func.isRequired,
  showNotes: PropTypes.bool,
  notes: PropTypes.array,
  onPostNote: PropTypes.func,
  onEditNote: PropTypes.func,
};

export default HuddleVisitCard;
